import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import settings from 'settings'
import { Box, CircularProgress, styled, Divider, Fade, Slide } from '@mui/material'
import { Alert, Button, IconButton, Link } from '@carto/meridian-ds/components'
import { HelpOutlineOutlined } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'app/state'
import { RequestStatus } from 'features/common'
import { setHelpPanelOpen } from 'features/help-panel/state/helpPanelSlice'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import { Client, getClient } from 'features/common/utils/clientParameter'
import { useCartoVersion } from 'features/common/hooks/useCartoVersion'

import HelpHeader from './HelpHeader'
import QuestionPanel from './QuestionPanel'
import HelpFooter from './HelpFooter'

const PanelBackdrop = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(6),
  left: 0,
  bottom: 0,
  width: '100%',
  backgroundColor: theme.palette.black[60]
}))

const Panel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  top: theme.spacing(6),
  right: 0,
  bottom: 0,
  maxWidth: '100%',
  width: theme.spacing(75),
  backgroundColor: theme.palette.background.paper,

  '& .MuiTypography-root': {
    color: theme.palette.text.primary
  }
}))

const PanelContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 3, 0, 3),
  flex: '1 1 auto',
  display: 'inline-flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  overflowX: 'hidden',
  overflowY: 'hidden'
}))

const ToggleIconWrapper = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'displayLabel'
})<{
  isOpen?: boolean
  displayLabel: boolean
}>(({ isOpen, displayLabel, theme }) => ({
  '&.MuiButton-root svg path': {
    fill: theme.palette.brand.appBarContrastText
  },
  '&:hover': {
    backgroundColor: theme.palette.white[12]
  },
  '& .MuiButton-startIcon': {
    marginLeft: 0
  },

  ...(!displayLabel && {
    minWidth: 0,
    padding: 0,

    '& .MuiButton-startIcon': {
      margin: `0 ${theme.spacing(0.875)}`
    },
    '& .MuiTypography-root': {
      display: 'none'
    }
  }),

  ...(isOpen && {
    backgroundColor: theme.palette.white[12]
  }),

  [theme.breakpoints.down('sm')]: {
    minWidth: 0,
    padding: 0,
    '& .MuiButton-startIcon': {
      margin: `0 ${theme.spacing(0.875)}`
    },
    '& .MuiTypography-root': {
      display: 'none'
    }
  }
}))

const LoadingIcon = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.brand.appBarContrastText
}))

export function HelpPanel() {
  const dispatch = useAppDispatch()
  const client = getClient()
  const { isSaaS } = useCartoVersion()
  const params = useParams()
  const isParamsEmpty = params && Object.keys(params).length === 0
  const displayLabel =
    client === Client.Workspace ||
    ((client === Client.WorkflowsUI || client === Client.WorkflowsSchedule) && isParamsEmpty)

  const { open, featuresStatus } = useAppSelector((state) => state.helpPanel)
  const loading = featuresStatus === RequestStatus.Pending

  const handleToggle = () => {
    if (!open) {
      dispatch(createMetricRequest(MetricEventType.HelpSidebarOpened))
    }
    dispatch(setHelpPanelOpen(!open))
  }

  if (loading) return <IconButton icon={<LoadingIcon size={18} />} />

  const NotAvailableYet = () => (
    <Alert severity="info">
      <FormattedMessage
        id="helpPanel.notAvailableYet"
        values={{
          a: (url: string) => (
            <Link href={settings.documentationUrl} external>
              {url}
            </Link>
          )
        }}
      />
    </Alert>
  )

  return (
    <>
      <ToggleIconWrapper
        isOpen={open}
        onClick={handleToggle}
        startIcon={<HelpOutlineOutlined />}
        displayLabel={displayLabel}
      >
        <FormattedMessage id="helpPanel.buttonLabel" />
      </ToggleIconWrapper>
      <Fade in={open} mountOnEnter unmountOnExit>
        <PanelBackdrop onClick={() => dispatch(setHelpPanelOpen(false))} />
      </Fade>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Panel>
          <PanelContent>
            <HelpHeader />
            {isSaaS ? <QuestionPanel /> : <NotAvailableYet />}
          </PanelContent>
          <Divider />
          <HelpFooter />
        </Panel>
      </Slide>
    </>
  )
}

export default HelpPanel
